import React, { useState, useContext } from 'react';
import { eventsApi } from '../../api';
import { UserContext } from '../../context/UserContext';
import HrnbModal from '../controls/HrnbModal';
import HrnbForm from '../controls/HrnbForm';
import HrnbFormControl from '../controls/HrnbFormControl';
import HrnbSelect from '../controls/HrnbSelect';
import HrnbDatePicker from '../controls/HrnbDatePicker';
import HrnbButton from '../controls/HrnbButton';
import HrnbAlert from '../controls/HrnbAlert';

const EVENT_TYPE_OPTIONS = [
    { value: 'BATTLE_START', label: 'Начало баттла' },
    { value: 'ROUND_START', label: 'Начало раунда' },
    { value: 'ROUND_END', label: 'Конец раунда' },
    { value: 'VOTING_START', label: 'Начало голосования' },
    { value: 'VOTING_END', label: 'Конец голосования' },
    { value: 'REGISTRATION_DEADLINE', label: 'Дедлайн регистрации' },
    { value: 'TRACK_UPLOAD_DEADLINE', label: 'Дедлайн загрузки треков' },
    { value: 'LIVE_STREAM', label: 'Прямая трансляция' },
    { value: 'MEETING', label: 'Встреча' },
    { value: 'CUSTOM', label: 'Пользовательское' },
];

const STATUS_OPTIONS = [
    { value: 'DRAFT', label: 'Черновик' },
    { value: 'PUBLISHED', label: 'Опубликовано' },
    { value: 'CANCELLED', label: 'Отменено' },
];

const TIMEZONE_OPTIONS = [
    { value: 'Europe/Moscow', label: 'Москва (UTC+3)' },
    { value: 'Europe/Kaliningrad', label: 'Калининград (UTC+2)' },
    { value: 'Europe/Samara', label: 'Самара (UTC+4)' },
    { value: 'Asia/Yekaterinburg', label: 'Екатеринбург (UTC+5)' },
    { value: 'Asia/Novosibirsk', label: 'Новосибирск (UTC+7)' },
    { value: 'Asia/Vladivostok', label: 'Владивосток (UTC+10)' },
];

export default function CreateEventModal({ open, onClose, onSuccess }) {
    const { currentUser } = useContext(UserContext);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [eventType, setEventType] = useState('CUSTOM');
    const [startDate, setStartDate] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endDate, setEndDate] = useState('');
    const [endTime, setEndTime] = useState('');
    const [timezone, setTimezone] = useState('Europe/Moscow');
    const [location, setLocation] = useState('');
    const [url, setUrl] = useState('');
    const [status, setStatus] = useState('PUBLISHED');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [errors, setErrors] = useState({});

    const resetForm = () => {
        setTitle('');
        setDescription('');
        setEventType('CUSTOM');
        setStartDate('');
        setStartTime('');
        setEndDate('');
        setEndTime('');
        setTimezone('Europe/Moscow');
        setLocation('');
        setUrl('');
        setStatus('PUBLISHED');
        setError(null);
        setErrors({});
    };

    const handleClose = () => {
        resetForm();
        if (onClose) onClose();
    };

    const validate = () => {
        const newErrors = {};
        if (!title || title.length < 3) {
            newErrors.title = 'Название должно быть не менее 3 символов';
        }
        if (!eventType) {
            newErrors.eventType = 'Выберите тип события';
        }
        if (!startDate || !startTime) {
            newErrors.startTime = 'Укажите дату и время начала';
        }
        if (endDate && endTime) {
            const start = new Date(`${startDate}T${startTime}`);
            const end = new Date(`${endDate}T${endTime}`);
            if (end <= start) {
                newErrors.endTime = 'Окончание должно быть позже начала';
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        setError(null);

        try {
            const payload = {
                title,
                description,
                eventType,
                startTime: `${startDate} ${startTime}`,
                timezone,
                status,
            };

            if (endDate && endTime) {
                payload.endTime = `${endDate} ${endTime}`;
            }
            if (location) payload.location = location;
            if (url) payload.url = url;
            if (currentUser?.id) payload.organizerId = currentUser.id;

            await eventsApi.create(payload);
            if (onSuccess) onSuccess();
            handleClose();
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || 'Ошибка при создании события';
            setError(msg);
            console.error('[CreateEventModal] Failed to create event:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <HrnbModal open={open} onClose={handleClose} title="Создать событие">
            <HrnbForm onSubmit={handleSubmit}>
                {error && <HrnbAlert type="danger">{error}</HrnbAlert>}

                <HrnbFormControl
                    label="Название *"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    error={errors.title}
                    placeholder="Название события"
                    autoFocus
                />

                <HrnbFormControl
                    label="Описание"
                    as="textarea"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Описание события"
                    style={{ marginTop: 12 }}
                />

                <HrnbSelect
                    label="Тип события *"
                    value={eventType}
                    onChange={setEventType}
                    options={EVENT_TYPE_OPTIONS}
                    error={errors.eventType}
                    style={{ marginTop: 12 }}
                />

                <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <HrnbDatePicker
                        label="Дата начала *"
                        value={startDate}
                        onChange={setStartDate}
                        error={errors.startTime}
                    />
                    <HrnbFormControl
                        label="Время начала *"
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        error={errors.startTime}
                    />
                </div>

                <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <HrnbDatePicker
                        label="Дата окончания"
                        value={endDate}
                        onChange={setEndDate}
                        error={errors.endTime}
                    />
                    <HrnbFormControl
                        label="Время окончания"
                        type="time"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        error={errors.endTime}
                    />
                </div>

                <HrnbSelect
                    label="Часовой пояс"
                    value={timezone}
                    onChange={setTimezone}
                    options={TIMEZONE_OPTIONS}
                    style={{ marginTop: 12 }}
                />

                <HrnbFormControl
                    label="Место проведения"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="Адрес или онлайн-платформа"
                    style={{ marginTop: 12 }}
                />

                <HrnbFormControl
                    label="Ссылка"
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://..."
                    style={{ marginTop: 12 }}
                />

                <HrnbSelect
                    label="Статус"
                    value={status}
                    onChange={setStatus}
                    options={STATUS_OPTIONS}
                    style={{ marginTop: 12 }}
                />

                <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
                    <HrnbButton variant="outline" onClick={handleClose} disabled={loading}>
                        Отмена
                    </HrnbButton>
                    <HrnbButton type="submit" variant="primary" disabled={loading}>
                        {loading ? 'Создание...' : 'Создать'}
                    </HrnbButton>
                </div>
            </HrnbForm>
        </HrnbModal>
    );
}